import React from 'react';
import { Link } from 'react-router-dom';
import { Lock, Briefcase, HeartHandshake, Globe, Compass, FileText, Sparkles, ArrowRight } from 'lucide-react';
import { useToolAccess } from './ToolGate';
import { useAuth } from '../../App';

const CLARITY_TOOLS = [
  ['clarity_hub', 'Clarity Hub Home', 'Your business vault & strategy', '/portal/clarity-hub', Compass],
  ['jumpstart', '7-Stage Business Jumpstart', 'Build your business blueprint', '/portal/clarity-hub', Briefcase],
  ['nonprofit', 'Nonprofit Launch Accelerator', 'Launch your nonprofit in 90 days', '/portal/clarity-hub', HeartHandshake],
  ['website_vision', 'Website Vision Builder', 'Design your website concept', '/portal/clarity-hub', Globe],
  ['blueprint', 'My Blueprint', 'Your strategy roadmap', '/portal/my-blueprint', FileText],
];

const ToolCard = ({ id, title, sub, to, Icon, open, pending }) => (
  <Link to={to} data-testid={`my-tool-${id}`}
    className="group rounded-xl border p-4 flex items-center gap-3 transition-transform hover:-translate-y-0.5"
    style={{ background: 'var(--p-surface)', borderColor: 'var(--p-border)' }}>
    <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${open ? 'bg-[#1D4ED8] text-white' : 'bg-slate-200 text-slate-500'}`}><Icon size={18} /></span>
    <span className="min-w-0 flex-1">
      <span className="block font-semibold text-sm truncate" style={{ color: 'var(--p-text)' }}>{title}</span>
      <span className="block text-xs truncate" style={{ color: 'var(--p-muted)' }}>{open ? sub : pending ? 'Access requested' : 'Unlocks with your coaching plan'}</span>
    </span>
    {open ? <ArrowRight size={15} className="text-[#1D4ED8] opacity-0 group-hover:opacity-100 transition-opacity" />
      : <Lock size={15} className="text-[#F97316]" data-testid={`my-tool-lock-${id}`} />}
  </Link>
);

export default function MyToolsGrid() {
  const { user } = useAuth();
  const { loading, has, pending } = useToolAccess();
  if (loading) return null;
  const studioOpen = ['admin', 'super_admin'].includes(user?.role) || user?.is_studio_approved === true;
  return (
    <section className="mb-8" data-testid="my-tools-grid">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-xs tracking-[0.2em] uppercase font-semibold" style={{ color: 'var(--p-faint)' }}>Your coaching tools</div>
        <span className="text-xs" style={{ color: 'var(--p-faint)' }}>Locked tools: tap to request access</span>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border p-4" style={{ background: 'var(--p-surface-2)', borderColor: 'var(--p-border)' }} data-testid="tools-group-clarity">
          <div className="flex items-center gap-2 mb-3 px-1">
            <Compass size={15} className="text-[#1D4ED8]" />
            <span className="text-sm font-semibold">Clarity Hub</span>
            <span className="text-xs" style={{ color: 'var(--p-faint)' }}>Plan your business, nonprofit & website</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {CLARITY_TOOLS.map(([id, title, sub, to, Icon]) => (
              <ToolCard key={id} id={id} title={title} sub={sub} to={to} Icon={Icon} open={has(id)} pending={pending(id)} />
            ))}
          </div>
        </div>
        <div className="rounded-2xl border p-4 flex flex-col" style={{ background: 'var(--p-surface-2)', borderColor: 'var(--p-border)' }} data-testid="tools-group-advanced">
          <div className="flex items-center gap-2 mb-3 px-1">
            <Sparkles size={15} className="text-[#7C3AED]" />
            <span className="text-sm font-semibold">Advanced tools</span>
          </div>
          <ToolCard id="social_studio" title="Social Studio" sub="AI viral content engine" to="/portal/studio" Icon={Sparkles} open={studioOpen} />
          <p className="text-xs mt-3 px-1 leading-relaxed" style={{ color: 'var(--p-faint)' }}>Generate viral scripts, posts and up to 90-day content plans tailored to your brand.</p>
        </div>
      </div>
    </section>
  );
}
